import clsx from 'clsx'
import { type Ref, computed, defineComponent, onMounted, ref } from 'vue'
import { RouterLink, type RouteLocationRaw } from 'vue-router'

interface Props {
  mainRef?: Ref<HTMLElement | null>
  tData?: string
  id?: string
  class?: unknown
  color?: string
  type?: 'button' | 'submit'
  autofocus?: boolean
  disabled?: boolean
  fakeDisabled?: boolean
  actived?: boolean
  to?: RouteLocationRaw
  onClick?: ((payload: MouseEvent) => void)
  onMouseup?: ((payload: MouseEvent) => void)
  onMousedown?: ((payload: MouseEvent) => void)
  onTouchend?: ((payload: TouchEvent) => void)
  onTouchstart?: (e: TouchEvent) => void
  onTouchmove?: (e: TouchEvent) => void
}

export default defineComponent<Props>((props, context) => {
  const buttonElm = ref<HTMLButtonElement | null>(null)

  // #region props Getters

  const tData = computed(() => props.tData ?? 'v-button')

  const colorClass = computed((): string | undefined => {
    return props.color ?? clsx('btn-primary')
  })

  const type = computed((): Props['type'] => {
    return props.type ?? 'button'
  })

  // #endregion

  onMounted(() => {
    if (props.autofocus) {
      setTimeout(() => buttonElm.value?.focus(), 0)
    }
  })

  return () => {
    if (props.to !== undefined) {
      return (
        <RouterLink
          t-data={tData.value}
          class={['btn', colorClass.value, props.class]}
          to={props.to}
        >
          {context.slots.default?.()}
        </RouterLink>
      )
    }

    return (
      <button
        ref={(ref) => {
          buttonElm.value = ref as HTMLButtonElement

          if (props.mainRef?.value !== undefined) {
            props.mainRef.value = ref as HTMLButtonElement
          }
        }}
        t-data={tData.value}
        id={props.id}
        class={['btn', colorClass.value, props.class]}
        type={type.value}
        autofocus={props.autofocus}
        disabled={props.disabled}
        aria-checked={props.actived}
        aria-disabled={props.fakeDisabled}
        onClick={props.onClick}
        onMouseup={props.onMouseup}
        onMousedown={props.onMousedown}
        onTouchend={props.onTouchend}
        onTouchstart={props.onTouchstart}
        onTouchmove={props.onTouchmove}
      >
        {context.slots.default?.()}
      </button>
    )
  }
}, {
  name: 'VButton',
  props: [
    'mainRef',
    'tData',
    'id',
    'class',
    'color',
    'type',
    'autofocus',
    'disabled',
    'fakeDisabled',
    'actived',
    'to',
    'onClick',
    'onMouseup',
    'onMousedown',
    'onTouchend',
    'onTouchstart',
    'onTouchmove'
  ]
})
